import React, { useMemo } from 'react';
import { Alert, Button, Dropdown, message, Modal, Popover, Space, Tag, Tooltip, Typography } from 'antd';
import {
    DownOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    QuestionCircleOutlined,
    ReloadOutlined,
    SyncOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * Flight Log 列表二级机号页签栏 + 右侧动作工具栏与链状态提示条（T08、T09⑤、T35、D29、D32、D48、D55、D56）
 *
 * 规则：
 * 1. 页签顺序固定：「全部」 -> 「未识别机号」（当 hasUnassigned 为真或当前正选中时显示） -> 各归一化机号（字母序）。
 * 2. 页签只展示机号文本，绝不展示任何记录数、断链数或悬浮数字（D29）。
 * 3. 右侧收拢图例说明气泡、对齐/补全、链序重算、导出当前筛选，避免多行散落。
 */
const FlightLogAircraftTabs = ({
    activeTab,
    aircraftRegs = [],
    hasUnassigned = false,
    chainIsComputing = false,
    chainOrderStale = false,
    recomputingChains = false,
    onSelectTab,
    onRefresh,
    onRecomputeChains,
    reconcileAction = null,
    exportAction = null,
}) => {
    const currentView = activeTab?.view || 'all';
    const currentReg = activeTab?.exactReg || null;

    const triggerRecompute = (onlyCurrentReg = false) => {
        if (!onRecomputeChains) return;
        onRecomputeChains({
            onlyCurrentReg,
            onSuccess: (res, meta) => {
                if (meta?.onlyCurrentReg && meta?.exactReg) {
                    message.success(`已刷新机号 ${meta.exactReg} 的首尾衔接链序`);
                } else {
                    const count = Array.isArray(res?.recomputed_regs) ? res.recomputed_regs.length : (res?.recomputed_count ?? '');
                    message.success(count !== '' ? `已完成全量链序初始化（共重算 ${count} 个机号）` : '已完成全量链序初始化');
                }
            },
        });
    };

    const confirmFullRecompute = () => {
        Modal.confirm({
            title: '确认对全库历史数据执行全量链序初始化/重算？',
            icon: <ExclamationCircleOutlined />,
            content: '用于数据库迁移后一键回填所有机号的首尾衔接链序与 Block Hours 校验。',
            okText: '立即全量初始化',
            cancelText: '取消',
            onOk: () => triggerRecompute(false),
        });
    };

    const mergedRegs = useMemo(() => {
        const list = Array.isArray(aircraftRegs) ? [...aircraftRegs] : [];
        if (currentView === 'reg' && currentReg && !list.includes(currentReg)) {
            list.push(currentReg);
            list.sort();
        }
        return list;
    }, [aircraftRegs, currentView, currentReg]);

    const showUnassigned = Boolean(hasUnassigned || currentView === 'unassigned');

    const handleTabClick = (view, exactReg = null) => {
        if (currentView === view && (view !== 'reg' || currentReg === exactReg)) {
            return;
        }
        if (onSelectTab) {
            onSelectTab({ view, exactReg });
        }
    };

    const getTabButtonStyle = (isActive) => ({
        flexShrink: 0,
        fontWeight: isActive ? 600 : 400,
    });

    const legendContent = (
        <Space direction="vertical" size={6} style={{ maxWidth: 360, fontSize: 12 }}>
            <div>
                <Tag color="error" icon={<ExclamationCircleOutlined />} style={{ marginRight: 6 }}>冲突</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>上一段到达站与本段出发站不一致（或中间漏传航段）</Text>
            </div>
            <div>
                <Tag color="default" icon={<InfoCircleOutlined />} style={{ marginRight: 6 }}>识别问题</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>出发站或到达站为空/未识别，无法比较</Text>
            </div>
            <div>
                <Tag color="warning" style={{ marginRight: 6 }}>时间顺序异常</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>本段撤轮挡时刻早于上一段到达时刻</Text>
            </div>
            <div>
                <Tag color="orange" style={{ marginRight: 6 }}>时间可能识别错误</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>轮挡起止推算时长与 Block Hours 相差超过 1 小时</Text>
            </div>
            <div>
                <Tag color="purple" style={{ marginRight: 6 }}>疑似重复</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>同机号 + 同 TL No. 非主行，不参与断链判定</Text>
            </div>
            <div>
                <Tag color="default" style={{ marginRight: 6 }}>无法定位</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>飞行日期为空，排在已定位航段之后</Text>
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 6, color: '#8c8c8c' }}>
                列头固定升序不可点击 · 点行打开右侧核对抽屉 · 切换页签清空勾选
            </div>
        </Space>
    );

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                    paddingBottom: 6,
                    borderBottom: '1px solid #f0f0f0',
                }}
            >
                <div
                    role="tablist"
                    aria-label="机号二级页签"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        overflowX: 'auto',
                        flex: 1,
                        minWidth: 240,
                    }}
                >
                    <Button
                        role="tab"
                        aria-selected={currentView === 'all'}
                        size="small"
                        type={currentView === 'all' ? 'primary' : 'default'}
                        style={getTabButtonStyle(currentView === 'all')}
                        onClick={() => handleTabClick('all', null)}
                    >
                        全部
                    </Button>

                    {showUnassigned && (
                        <Button
                            role="tab"
                            aria-selected={currentView === 'unassigned'}
                            size="small"
                            type={currentView === 'unassigned' ? 'primary' : 'default'}
                            style={getTabButtonStyle(currentView === 'unassigned')}
                            onClick={() => handleTabClick('unassigned', null)}
                        >
                            未识别机号
                        </Button>
                    )}

                    {mergedRegs.map((reg) => {
                        const isActive = currentView === 'reg' && currentReg === reg;
                        return (
                            <Button
                                key={reg}
                                role="tab"
                                aria-selected={isActive}
                                size="small"
                                type={isActive ? 'primary' : 'default'}
                                style={getTabButtonStyle(isActive)}
                                onClick={() => handleTabClick('reg', reg)}
                            >
                                {reg}
                            </Button>
                        );
                    })}
                </div>

                <Space size={6} style={{ flexShrink: 0 }}>
                    <Popover content={legendContent} title="首尾衔接与核对图例" placement="bottomRight">
                        <Button size="small" type="text" icon={<QuestionCircleOutlined />} style={{ color: '#595959' }}>
                            图例
                        </Button>
                    </Popover>
                    {reconcileAction}
                    {onRecomputeChains && (
                        <Space.Compact size="small">
                            <Tooltip
                                title={
                                    currentView === 'reg' && currentReg
                                        ? `重算当前机号 ${currentReg} 的首尾衔接链序`
                                        : '全量重算所有机号的首尾衔接链序'
                                }
                            >
                                <Button
                                    size="small"
                                    icon={<SyncOutlined />}
                                    loading={recomputingChains}
                                    onClick={() => {
                                        if (currentView === 'reg' && currentReg) {
                                            triggerRecompute(true);
                                        } else {
                                            confirmFullRecompute();
                                        }
                                    }}
                                >
                                    重算链序
                                </Button>
                            </Tooltip>
                            <Dropdown
                                placement="bottomRight"
                                menu={{
                                    items: [
                                        ...(currentView === 'reg' && currentReg
                                            ? [
                                                {
                                                    key: 'recompute-current',
                                                    icon: <SyncOutlined />,
                                                    label: `仅重算当前机号 (${currentReg})`,
                                                    onClick: () => triggerRecompute(true),
                                                },
                                            ]
                                            : []),
                                        {
                                            key: 'recompute-all',
                                            icon: <SyncOutlined />,
                                            label: '全量初始化所有机号链序',
                                            onClick: confirmFullRecompute,
                                        },
                                    ],
                                }}
                            >
                                <Button size="small" icon={<DownOutlined />} disabled={recomputingChains} />
                            </Dropdown>
                        </Space.Compact>
                    )}
                    {exportAction}
                </Space>
            </div>

            {currentView === 'reg' && chainIsComputing && (
                <Alert
                    type="info"
                    showIcon
                    icon={<SyncOutlined spin />}
                    message="断链状态计算中"
                    description="该机号下存在尚未完成首尾衔接计算的记录（例如刚执行完数据库迁移尚未初始化），计算完成前暂不展示断链提示条。"
                    action={
                        <Space size={6}>
                            {onRecomputeChains && (
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<SyncOutlined />}
                                    loading={recomputingChains}
                                    onClick={() => triggerRecompute(false)}
                                >
                                    一键初始化全量链序
                                </Button>
                            )}
                            {onRefresh && (
                                <Button size="small" icon={<ReloadOutlined />} onClick={onRefresh}>
                                    刷新
                                </Button>
                            )}
                        </Space>
                    }
                />
            )}

            {currentView === 'reg' && chainOrderStale && (
                <Alert
                    type="warning"
                    showIcon
                    message="顺序已更新，点击刷新"
                    action={
                        onRefresh ? (
                            <Button size="small" type="primary" icon={<ReloadOutlined />} onClick={onRefresh}>
                                立即刷新
                            </Button>
                        ) : null
                    }
                />
            )}
        </Space>
    );
};

export default FlightLogAircraftTabs;
